/**
 * Adapter de Storage para Supabase
 * Implementa StorageAdapter usando this.getClient().storage
 */
import { supabase } from '@/lib/supabase'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type {
  StorageAdapter,
  UploadOptions,
  UploadResult,
  ListOptions,
  StorageFile,
} from './types'

export class SupabaseStorageAdapter implements StorageAdapter {
  /**
   * @param client - Cliente Supabase autenticado (token fresco por request).
   *   Si se omite, cae al cliente global (`@/lib/supabase`) por compatibilidad.
   *   Inyectar el cliente del context evita JWT vencidos en Storage tras
   *   suspend/background: el cliente global solo refresca el token cada 50 min.
   */
  private readonly client?: SupabaseClient<Database>

  constructor(client?: SupabaseClient<Database>) {
    this.client = client
  }

  private getClient(): SupabaseClient<Database> {
    const client = this.client ?? supabase
    if (!client) {
      throw new Error('Supabase no está configurado')
    }
    return client
  }

  async upload(
    bucket: string,
    path: string,
    file: File | Blob,
    options?: UploadOptions
  ): Promise<UploadResult> {
    const { data, error } = await this.getClient().storage
      .from(bucket)
      .upload(path, file, {
        contentType: options?.contentType,
        upsert: options?.upsert ?? false,
      })

    if (error) {
      throw new Error(`Error al subir archivo: ${error.message}`)
    }

    return {
      path: data.path,
      id: data.id,
    }
  }

  async download(bucket: string, path: string): Promise<Blob> {
    const { data, error } = await this.getClient().storage
      .from(bucket)
      .download(path)

    if (error) {
      throw new Error(`Error al descargar archivo: ${error.message}`)
    }

    return data
  }

  async delete(bucket: string, paths: string[]): Promise<void> {
    const { error } = await this.getClient().storage
      .from(bucket)
      .remove(paths)

    if (error) {
      throw new Error(`Error al eliminar archivos: ${error.message}`)
    }
  }

  async getSignedUrl(
    bucket: string,
    path: string,
    expiresIn: number = 3600
  ): Promise<string> {
    const { data, error } = await this.getClient().storage
      .from(bucket)
      .createSignedUrl(path, expiresIn)

    if (error) {
      throw new Error(`Error al crear URL firmada: ${error.message}`)
    }

    return data.signedUrl
  }

  async list(
    bucket: string,
    path?: string,
    options?: ListOptions
  ): Promise<StorageFile[]> {
    const { data, error } = await this.getClient().storage
      .from(bucket)
      .list(path ?? '', {
        limit: options?.limit,
        offset: options?.offset,
        sortBy: options?.sortBy,
      })

    if (error) {
      throw new Error(`Error al listar archivos: ${error.message}`)
    }

    return data.map((file) => ({
      name: file.name,
      id: file.id,
      size: file.metadata?.size,
      contentType: file.metadata?.mimetype,
      createdAt: file.created_at,
      updatedAt: file.updated_at,
    }))
  }

  async exists(bucket: string, path: string): Promise<boolean> {
    try {
      // Intentamos obtener info del archivo
      const { data, error } = await this.getClient().storage
        .from(bucket)
        .list(path.split('/').slice(0, -1).join('/') || '', {
          limit: 1,
          search: path.split('/').pop(),
        })

      if (error) return false

      const fileName = path.split('/').pop()
      return data.some((file) => file.name === fileName)
    } catch {
      return false
    }
  }

  async copy(bucket: string, fromPath: string, toPath: string): Promise<void> {
    const { error } = await this.getClient().storage
      .from(bucket)
      .copy(fromPath, toPath)

    if (error) {
      throw new Error(`Error al copiar archivo: ${error.message}`)
    }
  }

  async move(bucket: string, fromPath: string, toPath: string): Promise<void> {
    const { error } = await this.getClient().storage
      .from(bucket)
      .move(fromPath, toPath)

    if (error) {
      throw new Error(`Error al mover archivo: ${error.message}`)
    }
  }
}

/**
 * Instancia singleton del adapter de Supabase
 */
export const supabaseStorage = new SupabaseStorageAdapter()
